<template>
    <div class="px-4 pt-14 pb-4">
        <div v-if="loading" class="flex justify-center items center">
            <div class="grid min-h-[140px] w-full place-items-center overflow-x-scroll rounded-lg p-6 lg:overflow-visible">
                <svg class="text-gray-300 animate-spin" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg"
                    width="24" height="24">
                    <path
                        d="M32 3C35.8083 3 39.5794 3.75011 43.0978 5.20749C46.6163 6.66488 49.8132 8.80101 52.5061 11.4939C55.199 14.1868 57.3351 17.3837 58.7925 20.9022C60.2499 24.4206 61 28.1917 61 32C61 35.8083 60.2499 39.5794 58.7925 43.0978C57.3351 46.6163 55.199 49.8132 52.5061 52.5061C49.8132 55.199 46.6163 57.3351 43.0978 58.7925C39.5794 60.2499 35.8083 61 32 61C28.1917 61 24.4206 60.2499 20.9022 58.7925C17.3837 57.3351 14.1868 55.199 11.4939 52.5061C8.801 49.8132 6.66487 46.6163 5.20749 43.0978C3.7501 39.5794 3 35.8083 3 32C3 28.1917 3.75011 24.4206 5.2075 20.9022C6.66489 17.3837 8.80101 14.1868 11.4939 11.4939C14.1868 8.80099 17.3838 6.66487 20.9022 5.20749C24.4206 3.7501 28.1917 3 32 3L32 3Z"
                        stroke="currentColor" stroke-width="5" stroke-linecap="round" stroke-linejoin="round"></path>
                    <path
                        d="M32 3C36.5778 3 41.0906 4.08374 45.1692 6.16256C49.2477 8.24138 52.7762 11.2562 55.466 14.9605C58.1558 18.6647 59.9304 22.9531 60.6448 27.4748C61.3591 31.9965 60.9928 36.6232 59.5759 40.9762"
                        stroke="currentColor" stroke-width="5" stroke-linecap="round" stroke-linejoin="round"
                        class="text-gray-900">
                    </path>
                </svg>
            </div>
        </div>

        <ul v-else>
            <div class="relative">
                <div class="pb-4 bg-white dark:bg-neutral-800 max-w-full">
                    <label for="table-search" class="sr-only">Search</label>
                    <div class="relative mt-1">
                        <div class="absolute inset-y-0 rtl:inset-r-0 start-0 flex items-center ps-3 pointer-events-none">
                            <svg class="w-4 h-4 text-gray-500 dark:text-neutral-400" aria-hidden="true"
                                xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 20">
                                <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z" />
                            </svg>
                        </div>
                        <input type="text" id="table-search" v-model="search" @input="handleSearch"
                            class="block pt-2 ps-10 text-sm text-gray-900 border border-gray-300 rounded-lg w-80 bg-gray-50 dark:bg-neutral-800 dark:border-neutral-600 dark:placeholder-neutral-400 dark:text-white max-w-full"
                            :placeholder="$t('Search for your promotions')">
                    </div>
                </div>
                <div class="overflow-x-auto space-y-3">
                    <table class="w-full text-sm text-left text-gray-500 dark:text-neutral-400">
                        <!-- Table head (hidden on mobile) -->
                        <thead
                            class="hidden md:table-header-group text-xs text-gray-700 uppercase bg-gray-50 dark:bg-neutral-800 dark:text-neutral-400">
                            <tr>
                                <th class="px-6 py-3">{{ $t('Name') }}</th>
                                <th class="px-6 py-3">{{ $t('Start') }}</th>
                                <th class="px-6 py-3">{{ $t('Duration') }}</th>
                                <th class="px-6 py-3">{{ $t('Type') }}</th>
                                <th class="px-6 py-3">{{ $t('Status') }}</th>
                                <th class="px-6 py-3"></th>
                            </tr>
                        </thead>

                        <tbody>
                            <tr v-for="item in items.data" :key="item.id"
                                class="relative block md:table-row mb-4 md:mb-0 rounded-xl py-2 md:py-0 md:rounded-none bg-white border md:border-0 md:border-b  dark:bg-neutral-800 dark:border-neutral-700 border-gray-200 hover:bg-gray-50 dark:hover:bg-neutral-600 transition">
                                <!-- Name -->
                                <td
                                    class="block md:table-cell px-4 py-2 md:px-2 md:py-4 font-medium text-gray-900 dark:text-white before:content-['Name:_'] md:before:content-none before:block before:font-semibold before:text-gray-600 dark:before:text-neutral-300">
                                    {{ getTranslated(item.resource?.title) }}
                                </td>

                                <!-- For small screens, combined block version -->
                                <td class="block md:hidden px-4 py-2 text-sm text-gray-700 dark:text-neutral-300">
                                    <div class="flex flex-wrap gap-x-4 gap-y-1">
                                        <div class="flex items-center gap-1">
                                            <span class="font-semibold">{{ $t('Start') }}:</span>
                                            <span>
                                                {{ new Date(item.resource?.start).toLocaleDateString('en-GB', {
                                                    day:
                                                        'numeric', month: 'short'
                                                }) }}
                                            </span>
                                        </div>
                                        <div class="flex items-center gap-1">
                                            <span class="font-semibold">{{ $t('Duration') }}:</span>
                                            <span>
                                                {{ getWeekLabel(item.resource?.start, item.resource?.end) }}
                                            </span>
                                        </div>
                                        <div class="flex items-center gap-1">
                                            <span class="font-semibold">{{ $t('Type') }}:</span>
                                            <span>{{ item.resource?.type }}</span>
                                        </div>
                                    </div>
                                </td>

                                <!-- For md+ screens, separate cells -->
                                <td class="hidden md:table-cell px-6 py-4">
                                    {{ new Date(item.resource?.start).toLocaleDateString('en-GB', {
                                        day: 'numeric',
                                        month: 'short',
                                    }) }}
                                </td>

                                <td class="hidden md:table-cell px-6 py-4">
                                    {{ getWeekLabel(item.resource?.start, item.resource?.end) }}
                                </td>

                                <td class="hidden md:table-cell px-6 py-4">
                                    {{ item.resource?.type }}
                                </td>


                                <td
                                    class="block md:table-cell px-4 py-2 md:px-2 md:py-4 relative 
                                    before:content-['Status:_'] before:font-semibold before:text-gray-600 
                                    dark:before:text-neutral-300 flex md:table-cell items-center md:items-center 
                                    justify-start gap-2 before:inline md:before:content-none md:text-center md:align-middle">
                                    <span class="px-3 py-1 rounded-full text-xs font-medium break-keep whitespace-nowrap"
                                        :class="getStatusPill(item.resource?.status).color">
                                        {{ getStatusPill(item.resource?.status).label }}
                                    </span>
                                </td>

                                <!-- Actions -->
                                <td
                                    class="absolute md:relative top-0 right-0 block md:table-cell px-4 py-2 md:px-2 md:py-4 flex md:table-cell justify-end md:justify-center items-center gap-2">
                                    <router-link :to="{ name: 'promotions.edit', params: { uuid: item.resource?.uuid } }"
                                        class="font-medium text-gray-500 hover:text-primary cursor-pointer dark:text-neutral-500 flex items-center">
                                        <svg-vue icon="pen" class="w-4 h-4 mr-1"></svg-vue>
                                        <span class="md:hidden">{{ $t('Edit') }}</span>
                                    </router-link>
                                </td>
                            </tr>
                        </tbody>
                    </table>

                    <div class="flex justify-between items-center gap-x-1" aria-label="Pagination">
                        <button type="button" :disabled="!items.prev_page_url" @click="changePage(items.current_page - 1)"
                            class="min-h-9.5 min-w-9.5 py-2 px-2.5 inline-flex justify-center items-center gap-x-2 text-sm rounded-lg text-gray-800 hover:bg-gray-100 focus:outline-hidden focus:bg-gray-100 disabled:opacity-50 disabled:pointer-events-none dark:text-white dark:hover:bg-white/10 dark:focus:bg-white/10"
                            aria-label="Previous">
                            <svg class="shrink-0 size-3.5" xmlns="http://www.w3.org/2000/svg" width="24" height="24"
                                viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                                stroke-linecap="round" stroke-linejoin="round">
                                <path d="m15 18-6-6 6-6"></path>
                            </svg>
                            <span aria-hidden="true" class="hidden sm:block">Previous</span>
                        </button>
                        <div class="flex items-center gap-x-1">
                            <span
                                class="min-h-9.5 flex justify-center items-center text-gray-500 py-2 px-1.5 text-sm dark:text-neutral-500">{{ items.current_page }} </span>
                            <span
                                class="min-h-9.5 flex justify-center items-center text-gray-500 py-2 px-1.5 text-sm dark:text-neutral-500">of</span>
                            <span
                                class="min-h-9.5 flex justify-center items-center text-gray-500 py-2 px-1.5 text-sm dark:text-neutral-500">{{ items.last_page }} </span>
                        </div>
                        <button type="button" :disabled="!items.next_page_url" @click="changePage(items.current_page + 1)"
                            class="min-h-9.5 min-w-9.5 py-2 px-2.5 inline-flex justify-center items-center gap-x-2 text-sm rounded-lg text-gray-800 hover:bg-gray-100 focus:outline-hidden focus:bg-gray-100 disabled:opacity-50 disabled:pointer-events-none dark:text-white dark:hover:bg-white/10 dark:focus:bg-white/10">
                            <span aria-hidden="true" class="hidden sm:block">Next</span>
                            <svg class="shrink-0 size-3.5" xmlns="http://www.w3.org/2000/svg" width="24" height="24"
                                viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                                stroke-linecap="round" stroke-linejoin="round">
                                <path d="m9 18 6-6-6-6"></path>
                            </svg>
                        </button>
                    </div>
                </div>
            </div>
        </ul>
    </div>
</template>
    
<script>
import dashboardHelpers from "../dashboard-helpers";
import debounce from "lodash/debounce";
export default {
    props: {
        accessTypes: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            loading: false,
            items: [],
            search: "",
            statusMap: {
                pending: { label: this.$t('Pending'), color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-800/50 dark:text-yellow-400' },
                paymentRequired: { label: this.$t('Payment Required'), color: 'bg-orange-100 text-orange-800 dark:bg-orange-800/50 dark:text-orange-400' },
                reviewRequired: { label: this.$t('Under Review'), color: 'bg-blue-100 text-blue-800 dark:bg-blue-800/50 dark:text-blue-400' },
                confirmed: { label: this.$t('Confirmed'), color: 'bg-green-100 text-green-800 dark:bg-green-800/50 dark:text-green-400' },
                cancelled: { label: this.$t('Cancelled'), color: 'bg-red-100 text-red-800 dark:bg-red-800/50 dark:text-red-400' }, // optional extra
            }
        }
    },
    async mounted() {
        this.fetchItems()
    },
    computed: {
        promoteText() {
            const hasEvent = this.accessTypes.includes('App\\Models\\Event');
            const hasLocation = this.accessTypes.includes('App\\Models\\Location');

            if (hasEvent && hasLocation) return this.$t('Promote your event or location');
            if (hasEvent) return this.$t('Promote your event');
            if (hasLocation) return this.$t('Promote your location');
            return this.$t('Promote');
        }
    },
    methods: {
        ...dashboardHelpers,
        async fetchItems(page = 1) {
            try {
                this.loading = true;

                const res = await axios.get("/api/dashboard/promotions", {
                    params: {
                        search: this.search || "",
                        page: page
                    }
                });

                this.items = res.data;
            } catch (error) {
                if (error.response?.status === 401) {
                    // User is unauthenticated → refresh token or redirect to login
                    console.warn('User unauthenticated. Attempting refresh...');
                    window.location.reload();
                } else {
                    console.error('Error fetching promotions:', error);
                }
            } finally {
                this.loading = false;
            }
        },
        changePage(page) {
            this.fetchItems(page);
        },
        getStatusPill(status) {
            return this.statusMap[status] || { label: status, color: 'bg-gray-100 text-gray-800' };
        },
        // ✅ Debounce search call
        handleSearch: debounce(function () {
            this.fetchItems()
        }, 300),
        getWeeks(start, end) {
            if (!start || !end) return 0;

            const startDate = new Date(start);
            const endDate = new Date(end);

            const diff = endDate - startDate; // difference in ms
            const weeks = Math.ceil(diff / (1000 * 60 * 60 * 24 * 7));

            return weeks;
        },

        getWeekLabel(start, end) {
            const weeks = this.getWeeks(start, end);
            return weeks + ' ' + (weeks === 1 ? this.$t('Week') : this.$t('Weeks'));
        }
    }
}
</script>
    